package floris.graphics.repo

import floris.graphics.NetworkingConfig
import floris.graphics.QueryOptions
import floris.graphics.deleteRequest
import floris.graphics.extensions.buildFormData
import floris.graphics.getRequest
import floris.graphics.models.NetworkResponse
import floris.graphics.models.domain.models.Techstack
import floris.graphics.models.domain.models.toTechstack
import floris.graphics.models.dto.requests.techstack.InsertTechstack
import floris.graphics.models.dto.requests.techstack.TechstackDto
import floris.graphics.models.dto.requests.techstack.UpdateTechstack
import floris.graphics.models.dto.responses.ErrorResponse
import floris.graphics.postRequest
import floris.graphics.putRequest
import org.w3c.files.File

object TechstackRepo {

    private val techstackUrl = "${NetworkingConfig.baseUrl}/api/v1/techstacks"

    suspend fun getTechstacks(queryOptions: QueryOptions = QueryOptions()): List<Techstack> {
        val url = techstackUrl
        val response: NetworkResponse<List<TechstackDto>, ErrorResponse> = getRequest(url, queryOptions, true)

        return when (response) {
            is NetworkResponse.Success -> response.body.map { it.toTechstack() }
            else -> emptyList()
        }
    }

    suspend fun getTechstack(techstackId: String): Techstack? {
        val url = "$techstackUrl/$techstackId"
        val response: NetworkResponse<TechstackDto, ErrorResponse> = getRequest(url, allowUnauthenticated = true)

        return when (response) {
            is NetworkResponse.Success -> response.body.toTechstack()
            else -> null
        }
    }

    suspend fun insertTechstack(techstack: InsertTechstack, image: File?): Techstack? {
        val url = techstackUrl

        val formData = buildFormData(
            data = techstack,
            extraFields = mapOf("image" to image).filterValues { it != null }
        )

        val response: NetworkResponse<TechstackDto?, ErrorResponse> = postRequest(
            resource = url,
            body = formData
        )

        return when (response) {
            is NetworkResponse.Success -> response.body?.toTechstack()
            else -> null
            // TODO [TicketNumber]: see if we want this instead of `null`
//            is NetworkResponse.ServerError -> throw RuntimeException(response.body?.errorDescription)
//            is NetworkResponse.Error -> throw response.error ?: Exception("Unknown error")
        }
    }

    suspend fun updateTechstack(techstackId: String, techstack: UpdateTechstack): Techstack? {
        val url = "$techstackUrl/$techstackId"
        val response: NetworkResponse<TechstackDto, ErrorResponse> = putRequest(resource = url, body = techstack)

        return when (response) {
            is NetworkResponse.Success -> response.body.toTechstack()
            else -> null
        }
    }

    suspend fun updateTechstackImage(techstackId: String, image: File): Techstack? {
        val url = "$techstackUrl/$techstackId/upload"

        val formData = buildFormData(
            extraFields = mapOf("image" to image)
        )

        val response: NetworkResponse<TechstackDto?, ErrorResponse> = putRequest(
            resource = url,
            body = formData
        )

        return when (response) {
            is NetworkResponse.Success -> response.body?.toTechstack()
            else -> null
        }
    }

    suspend fun deleteTechstack(techstackId: String): Boolean {
        val url = "$techstackUrl/$techstackId"
        val response = deleteRequest(url)

        return when (response) {
            is NetworkResponse.Success -> true
            else -> false
        }
    }
}
